/**
 * PatrolPuffin dedicated tenant auth-client pair.
 *
 * AUTHSVC-CONSOLIDATION-001 (Phase 3): the canonical `@puffin-core/auth-client`
 * `createAuthService` factory is transport-agnostic — it consumes an injected
 * `http` adapter built (via `createApiClientTransport`) over a PUBLIC + AUTHED
 * pair of `@puffin-core/api-client` instances that BOTH share the auth base URL
 * (`/api/v1/auth`).
 *
 * - `authedAuthClient` carries `getToken` + `onTokenRefresh` + `onRefreshFailed`
 *   so authenticated auth ops (passkey register/list/delete, tenant logout) get
 *   the canonical Bearer attach + 401-refresh behavior.
 * - `publicAuthClient` carries NEITHER — clean pre-auth calls (tenant/platform
 *   login, cross-app exchange, discoverable passkey options/authenticate) with
 *   no stale-token attach.
 *
 * The transport passes `authRequired: false` on every call (the factory gates
 * token presence itself via `mustHaveToken`), so the ABG-001 pre-flight is
 * skipped and the backend answers directly.
 *
 * @module core/api/auth/authClient
 */

import { createApiClient } from '@puffin-core/api-client';
import { authStorage, REFRESH_ENDPOINT } from './authStorage';

/** Auth base URL shared by both clients so the factory's base-relative paths resolve. */
const AUTH_BASE_URL = '/api/v1/auth';

/** Token-less public client — clean pre-auth auth calls (no stale-token attach). */
export const publicAuthClient = createApiClient({
  baseURL: AUTH_BASE_URL,
});

/** Authed client — Bearer attach + canonical 401-refresh for authenticated auth ops. */
export const authedAuthClient = createApiClient({
  baseURL: AUTH_BASE_URL,
  getToken: authStorage.getAccessToken,
  onTokenRefresh: () => authStorage.refreshAccessToken(REFRESH_ENDPOINT),
  onRefreshFailed: ({ reason }) => {
    authStorage.clearAuth();
    authStorage.emitExpiry({ reason });
  },
});
